'use client'

import { useInfiniteQuery } from '@tanstack/react-query'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons'
import { Fragment } from 'react'
import { createGlossarySummaryPageQuery } from '@/features/glossary/queries'
import TextButton from '@/shared/components/buttons/TextButton'
import Link from 'next/link'
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons'
import { useDebounce } from '@uidotdev/usehooks'
import { parseAsString, useQueryState } from 'nuqs'

const queryStateOptions = {
  clearOnDefault: true,
  history: 'replace',
  scroll: false,
} as const

export default function GlossaryPage() {
  const [firstChar, setFirstChar] = useQueryState(
    'letter',
    parseAsString.withDefault('').withOptions(queryStateOptions),
  )
  const [input, setInput] = useQueryState(
    'q',
    parseAsString.withDefault('').withOptions(queryStateOptions),
  )
  const inputDebounced = useDebounce(input, 300)
  const summariesQuery = useInfiniteQuery(
    createGlossarySummaryPageQuery(
      '',
      inputDebounced.length > 2 ? inputDebounced : '',
      firstChar,
    ),
  )

  const searchIsTooShort =
    inputDebounced.length === 1 || inputDebounced.length === 2

  return (
    <div className="mt-16 grid grid-cols-6 gap-x-4 gap-y-4 font-sans">
      <div className="col-span-6 space-y-6 lg:col-span-4">
        <div>
          <div className="grid w-full grid-cols-1">
            <input
              id="search"
              name="search"
              type="text"
              placeholder="Search"
              className="col-start-1 row-start-1 block w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-base text-gray-900 outline-0 placeholder:text-gray-400 focus:border-blue-600 focus:outline-0 sm:pl-9"
              value={input}
              onChange={(e) => void setInput(e.target.value)}
            />
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="pointer-events-none col-start-1 row-start-1 ml-3 size-5 self-center text-gray-400 sm:size-4"
            />
          </div>
          {searchIsTooShort && (
            <div className="mt-1 flex items-center gap-x-0.5 text-red-600">
              <FontAwesomeIcon icon={faCircleExclamation} className="mr-1" />{' '}
              <p className="text-sm">
                Search must be at least 3 characters long.
              </p>
            </div>
          )}
        </div>
        <div className="divide-y divide-gray-300 rounded-md border border-gray-300">
          {(() => {
            if (summariesQuery.isLoading) {
              return (
                <div className="flex items-center justify-center rounded-md bg-gray-200 py-6 text-gray-700">
                  Loading...
                </div>
              )
            } else if (
              summariesQuery.data?.pages.length === 1 &&
              summariesQuery.data.pages[0].length === 0
            ) {
              return (
                <div className="flex items-center justify-center rounded-md bg-gray-200 py-6 text-gray-700">
                  No terms found.
                </div>
              )
            } else {
              return summariesQuery.data?.pages.map((page, idx) => (
                <Fragment key={idx}>
                  {/* Only show the first 20 entries as the last one is purely for cursoring */}
                  {page.slice(0, 20).map((glossary) => (
                    <Link
                      key={glossary.slug.current}
                      className="block cursor-pointer space-y-1 px-3 py-2.5 transition-all hover:bg-gray-100"
                      href={`/glossary/${glossary.slug.current}`}
                    >
                      <div className="flex items-center justify-between text-gray-800">
                        <h3 className="font-bold leading-relaxed">
                          {glossary.term}
                        </h3>
                        <FontAwesomeIcon icon={faArrowRight} />
                      </div>
                      <p className="font-sans tracking-tight text-gray-500">
                        {glossary.summary}
                      </p>
                    </Link>
                  ))}
                </Fragment>
              ))
            }
          })()}
        </div>
        {summariesQuery.hasNextPage && (
          <TextButton
            title="Load More"
            color="blue"
            className="!px-8 !py-2.5 !text-base"
            onClick={() => summariesQuery.fetchNextPage()}
            loading={summariesQuery.isFetchingNextPage}
          />
        )}
      </div>
      <div className="col-span-6 lg:col-span-2">
        <div className="flex items-baseline gap-x-3">
          <p className="font-jakarta text-3xl font-bold tracking-tight text-gray-800">
            Filter
          </p>
          <button
            className="cursor-pointer text-gray-600 transition-colors hover:text-gray-700"
            onClick={() => void setFirstChar('')}
          >
            Reset
          </button>
        </div>
        <div className="mt-3 grid grid-cols-4 gap-1">
          {[
            'A',
            'B',
            'C',
            'D',
            'E',
            'F',
            'G',
            'H',
            'I',
            'J',
            'K',
            'L',
            'M',
            'N',
            'O',
            'P',
            'Q',
            'R',
            'S',
            'T',
            'U',
            'V',
            'W',
            'X',
            'Y',
            'Z',
            '#',
          ].map((letter) => (
            <div key={letter} className="flex items-center">
              <input
                id={letter}
                name="glossary-letter"
                type="radio"
                className="relative size-5 appearance-none rounded-full border border-gray-300 bg-white before:absolute before:inset-1 before:rounded-full before:bg-white checked:border-blue-600 checked:bg-blue-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:border-gray-300 disabled:bg-gray-100 disabled:before:bg-gray-400 forced-colors:appearance-auto forced-colors:before:hidden [&:not(:checked)]:before:hidden"
                checked={firstChar === letter}
                onChange={() => void setFirstChar(letter)}
              />
              <label
                htmlFor={letter}
                className="ml-3 block text-lg text-gray-700"
              >
                {letter}
              </label>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
